import React from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import DataTable from "../../../lib/components/DataTable";
import EmptyState from "../../../lib/components/EmptyState";
import organizationservice from "../../../service/organizationservice";
import errorHandler from "../../../utils/errorHandler";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../../lib/components/ConfirmationDialog";
import { MoreVert } from "@mui/icons-material";

const AdministratorsDataTable = ({
  data,
  isLoading,
  usersQuery,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}) => {
  const [user, setUser] = React.useState(null);
  const [isActivate, setIsActivate] = React.useState(false);
  const [isDeactivate, setIsDeactivate] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isMenu, setIsMenu] = React.useState(null);

  const activateMutation = useMutation({
    mutationFn: () => organizationservice.activateAdmin(user?.id),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      toast.success("Administrator activated successfully.");
      usersQuery.refetch();
      setIsActivate(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => organizationservice.deactivateAdmin(user?.id),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      toast.success("Administrator deactivated successfully.");
      usersQuery.refetch();
      setIsDeactivate(false);
    },
  });

  const superAdminMutation = useMutation({
    mutationFn: () => organizationservice.makeSuperAdmin(user?.id),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      toast.success("User role updated successfully.");
      usersQuery.refetch();
      setIsAdmin(false);
    },
  });

  if (isLoading) return null;

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (_, row) => row?.name,
    },
    {
      key: "email",
      header: "Email",
    },
    {
      key: "position",
      header: "Position",
    },
    {
      key: "role",
      header: "Role",
    },
    {
      key: "status",
      header: "Status",
      render: (_, row) => <Status status={row?.status} />,
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            setIsMenu(e.currentTarget);
            setUser(row);
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <DataTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      <EmptyState isEmpty={!isLoading && !data?.length} />

      <ConfirmationDialog
        isOpen={isActivate}
        type="confirm"
        title="Activate User"
        isLoading={activateMutation.isPending}
        onClose={() => setIsActivate(false)}
        onSubmit={() => activateMutation.mutate()}
        body={`You have selected to activate this user <span class="fw-medium">"${user?.name}</span>". Select "<span class="fw-medium">Yes, activate</span>" to continue.`}
        actionTitle="Yes, activate"
      />
      <ConfirmationDialog
        isOpen={isDeactivate}
        type="danger"
        title="Deactivate User"
        isLoading={deactivateMutation.isPending}
        onClose={() => setIsDeactivate(false)}
        onSubmit={() => deactivateMutation.mutate()}
        body={`You have selected to deactivate this user <span class="fw-medium">"${user?.name}</span>". Select "<span class="fw-medium">Yes, deactivate</span>" to continue.`}
        actionTitle="Yes, deactivate"
      />
      <ConfirmationDialog
        isOpen={isAdmin}
        type="confirm"
        title="Confirm Action"
        isLoading={superAdminMutation.isPending}
        onClose={() => setIsAdmin(false)}
        onSubmit={() => superAdminMutation.mutate()}
        body={`This action will make the user a Super Admin. Are you sure you want to proceed?`}
        actionTitle="Yes, confirm"
      />
      <Menu
        anchorEl={isMenu}
        open={Boolean(isMenu)}
        onClose={() => setIsMenu(null)}
      >
        <MenuItem
          onClick={() => setIsAdmin(true)}
          disabled={user?.role === "Super user"}
        >
          Make Super Admin
        </MenuItem>
        {user?.status === "ACTIVE" ? (
          <MenuItem
            onClick={() => {
              setIsDeactivate(true);
              setIsMenu(null);
            }}
            className="text-danger"
          >
            Deactivate User
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setIsActivate(true);
              setIsMenu(null);
            }}
            className="text-primary"
          >
            Activate User
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

function Status({ status }) {
  if (status === "ACTIVE")
    return (
      <div className="badge rounded-pill  fw-medium fs-sm bg-primary text-primary bg-opacity-25">
        Active
      </div>
    );
  if (status === "EXPIRED")
    return (
      <div className="badge bg-warning text-warning rounded-pill fw-medium fs-sm bg-opacity-25">
        Expired
      </div>
    );
  if (status === "INACTIVE")
    return (
      <div className="badge bg-danger text-danger rounded-pill fw-medium fs-sm bg-opacity-25">
        Inactive
      </div>
    );
  return null;
}

export default AdministratorsDataTable;
